import type { Building } from "building-types/building/buildingTypes";
import {
  ControllerSwitchType,
  ControllerType,
  ElectricalBoxType,
  LoadType,
} from "building-types/electrical/electricalTypesEnum";
import type {
  Controller,
  GeneratedElectricalGroup,
  StandardElectricalItem,
} from "building-types/electrical/generatedElectricalTypes";
import { faceIdsFromPlanarGraphsByPoint } from "geom/faceIdsFromPlanarGraphByPoint";
import { polyLine2Centroid } from "geom/polyLine2";

const GangBoxPrefix: string[] = ["", "ONE", "TWO", "THREE", "FOUR", "FIVE"];
export const ControllerTypeScheduleName: { [key in ControllerType]: string } = {
  [ControllerType.SWITCH]: "Switch",
  [ControllerType.SENSOR]: "Sensor",
  [ControllerType.OPENER]: "Opener",
};
export const ControllerSwitchTypeScheduleName: { [key in ControllerSwitchType]: string | undefined } = {
  [ControllerSwitchType.STANDARD]: undefined,
  [ControllerSwitchType.THREE_WAY]: "Three Way",
  [ControllerSwitchType.FOUR_WAY]: "Four Way",
  [ControllerSwitchType.DIMMER]: "Dimmer",
};

export function getElectricalItems(
  {
    electrical,
    planarGraphs,
    rooms,
    roomsByLevel,
    roomLevelIndices,
  }: Pick<Building, "electrical" | "planarGraphs" | "rooms" | "roomsByLevel" | "roomLevelIndices">,
  filter?: (entity: GeneratedElectricalGroup) => boolean,
): StandardElectricalItem[] {
  const unplacedElectricItems: StandardElectricalItem[] = [];
  const candidateGroups: {
    [candidateId: string]: Pick<StandardElectricalItem, "id" | "handle" | "roomHandle" | "position" | "normal"> & {
      count: number;
    };
  } = {};

  for (const group of electrical) {
    if (filter && !filter(group)) {
      continue;
    }

    for (const controller of group.controllers) {
      unplacedElectricItems.push(controllerEntityName(controller));
      if (controller.type === ControllerType.SWITCH) {
        // default here is a replacement for what used to be a "null" value
        candidateGroups[controller?.candidateId ?? "default"] = {
          count: (candidateGroups[controller?.candidateId ?? "default"]?.count || 0) + 1,
          id: controller.handle?.id ?? "",
          roomHandle: controller.sourceRoomHandle,
          handle: controller.handle,
          position: controller.position?.position,
          normal: controller.position?.normal,
        };
      }
    }
    for (const load of group.loads) {
      if (
        load.loadType === LoadType.FLOOR_TO_FLOOR_TRANSITION ||
        load.loadType === LoadType.VIRTUAL_CEILING_FAN_LIGHT ||
        load.loadType === LoadType.HOSE_BIB
      ) {
        continue;
      }

      const type = load.loadDescriptor?.name ?? "";
      unplacedElectricItems.push({
        id: load.handle?.id ?? "",
        handle: load.handle,
        type,
        group: "LOAD",
        reportedName: load.loadDescriptor?.planLabel,
        entityName: load.loadType,
        roomHandle: load.sourceRoomHandle,
        position: load.position,
        normal: load.normal,
      });

      if (load.loadDescriptor?.boxType && load.loadDescriptor.boxType !== ElectricalBoxType.NONE) {
        unplacedElectricItems.push(
          electricalBoxItem(load.loadDescriptor.boxType, {
            id: load.handle?.id ?? "",
            roomHandle: load.sourceRoomHandle,
            handle: load.handle,
            position: load.position,
            normal: load.normal,
          }),
        );
      }
    }
  }

  for (const { count: switchCount, id, roomHandle, position, normal } of Object.values(candidateGroups)) {
    let name: ElectricalBoxType;
    if (switchCount < 6) {
      name = ElectricalBoxType[`${GangBoxPrefix[switchCount]}_GANG`];
    } else if (switchCount > 10) {
      console.log(`A candidate group of switches contains ${switchCount} switches.`);
      continue;
    } else {
      const low = Math.floor(switchCount / 2);
      name = ElectricalBoxType[`${GangBoxPrefix[switchCount - low]}_GANG`];
      const otherName = ElectricalBoxType[`${GangBoxPrefix[low]}_GANG`];
      // first half of the split group added here, the second half is added below
      unplacedElectricItems.push(electricalBoxItem(otherName, { id, roomHandle, position, normal }));
    }

    unplacedElectricItems.push(electricalBoxItem(name, { id, roomHandle, position, normal }));
  }

  const placedElectricItems = unplacedElectricItems.map((item) =>
    placeElectricalItemInRoom(item, { planarGraphs, rooms, roomsByLevel, roomLevelIndices }),
  );

  return placedElectricItems;
}

function controllerEntityName(controller: Controller): StandardElectricalItem {
  if (controller.type === ControllerType.SWITCH) {
    const { switchType } = controller;
    const switchName = switchType ? ControllerSwitchTypeScheduleName[switchType] || "" : "";

    return {
      id: controller.handle?.id ?? "",
      type: `${ControllerTypeScheduleName[ControllerType.SWITCH]} ${switchName}`.trim(),
      group: "CONTROLLER",
      entityName: encodeControllerSwitchType(switchType),
      roomHandle: controller.sourceRoomHandle,
      position: controller.position?.position,
      normal: controller.position?.normal,
    };
  }

  return {
    id: controller.handle?.id ?? "",
    type: ControllerTypeScheduleName[controller.type],
    group: "CONTROLLER",
    entityName: controller.type,
    roomHandle: controller.sourceRoomHandle,
    position: controller.position?.position,
    normal: controller.position?.normal,
  };
}

function electricalBoxItem(
  boxType: ElectricalBoxType,
  {
    id,
    roomHandle,
    handle,
    position,
    normal,
  }: Pick<StandardElectricalItem, "id" | "handle" | "roomHandle" | "position" | "normal">,
): StandardElectricalItem {
  return {
    id,
    handle,
    type: boxType,
    group: "ELECTRICAL_BOX",
    entityName: boxType,
    roomHandle,
    position,
    normal,
  };
}

function encodeControllerSwitchType(switchType: ControllerSwitchType | undefined): string {
  return switchType ? `${ControllerType.SWITCH}_${switchType}` : `${ControllerType.SWITCH}_`;
}

export function decodeControllerSwitchType(entityName: string): [string, string | undefined] {
  const typeSeparatorIndex = entityName.indexOf("_");

  if (typeSeparatorIndex === -1) {
    return [entityName, undefined];
  }

  const type = entityName.substring(0, typeSeparatorIndex);
  const switchType = entityName.substring(typeSeparatorIndex + 1);

  return [type, switchType];
}

function placeElectricalItemInRoom(
  unplacedElectricItem: StandardElectricalItem,
  {
    planarGraphs,
    rooms,
    roomsByLevel,
    roomLevelIndices,
  }: Pick<Building, "planarGraphs" | "rooms" | "roomsByLevel" | "roomLevelIndices">,
): StandardElectricalItem {
  const { roomHandle } = unplacedElectricItem;
  const room = rooms[roomHandle.id];
  const { level } = room;

  const position = unplacedElectricItem.position ?? polyLine2Centroid(room.outline);

  const roomsFromPlanarGraph = faceIdsFromPlanarGraphsByPoint(
    position ? [position[0], position[1]] : null,
    planarGraphs,
    roomsByLevel,
    roomLevelIndices,
    [level.id],
  );

  if (roomsFromPlanarGraph.length === 0) {
    return unplacedElectricItem;
  }

  // return the first room that contains the point
  return { ...unplacedElectricItem, roomHandle: { type: "ROOM", id: roomsFromPlanarGraph[0] } };
}
